import { useEffect, useState } from "react";
import {
  CheckCircle2,
  AlertCircle,
  Database,
  RefreshCw,
  Activity,
  Sparkles,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { api } from "../../lib/api";

function StatusDot({ tone }) {
  const colors = {
    ok: "bg-emerald-500",
    error: "bg-red-500",
    disabled: "bg-slate-400",
  };
  return (
    <span
      className={`inline-flex h-2 w-2 rounded-full ${colors[tone] || "bg-slate-300"}`}
    />
  );
}

function Card({ icon: Icon, title, value, sub, tone, "data-testid": testId }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
          {title}
        </div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="text-3xl font-semibold text-slate-900 tracking-tight">
        {value}
      </div>
      {sub && (
        <div className="mt-2 text-xs text-slate-600 flex items-center gap-2">
          {tone && <StatusDot tone={tone} />}
          <span>{sub}</span>
        </div>
      )}
    </div>
  );
}

export default function AdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.health();
      setData(r);
    } catch (e) {
      setData({
        counts: {},
        services: {
          analisi: { status: "error", label: "Servizio analisi" },
          ricerca_intelligente: {
            enabled: false,
            status: "error",
            label: "Ricerca intelligente",
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (!data) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-8 text-slate-500">
        Caricamento...
      </div>
    );
  }

  const analisi = data.services?.analisi || {};
  const ricerca = data.services?.ricerca_intelligente || {};
  const analisiOk = analisi.status === "ok";
  const ricercaEnabled = !!ricerca.enabled;
  const ricercaOk = ricerca.status === "ok";

  return (
    <div className="space-y-5" data-testid="admin-health">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Stato sistema
          </h1>
          <p className="text-sm text-slate-500">
            Servizi attivi e contatori della piattaforma.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          className="rounded-md border-slate-300 gap-2"
          data-testid="health-refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          icon={Activity}
          title="Servizio analisi"
          value={analisiOk ? "OK" : "KO"}
          sub={analisiOk ? "Operativo" : "Non disponibile"}
          tone={analisiOk ? "ok" : "error"}
          data-testid="health-card-analisi"
        />
        <Card
          icon={Sparkles}
          title="Ricerca intelligente"
          value={
            ricercaEnabled ? (ricercaOk ? "Attiva" : "KO") : "Disattivata"
          }
          sub={
            ricercaEnabled
              ? ricercaOk
                ? "Operativa"
                : "Servizio non raggiungibile"
              : "Non in uso"
          }
          tone={
            ricercaEnabled
              ? ricercaOk
                ? "ok"
                : "error"
              : "disabled"
          }
          data-testid="health-card-ricerca"
        />
        <Card
          icon={Database}
          title="Listino articoli"
          value={data.counts?.listino_articoli ?? 0}
          sub="totale a sistema"
          data-testid="health-card-listino"
        />
        <Card
          icon={CheckCircle2}
          title="Sessioni"
          value={data.counts?.quote_sessions ?? 0}
          sub={`Audit log: ${data.counts?.audit_log ?? 0}`}
          data-testid="health-card-sessions"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">
          Diagnostica
        </h2>
        <ul className="text-sm space-y-2.5">
          <li className="flex items-center gap-3">
            {analisiOk ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            <div className="flex-1">
              <div className="text-sm text-slate-800">{analisi.label}</div>
              <div className="text-xs text-slate-500">
                {analisiOk
                  ? "Servizio attivo, in attesa di richieste."
                  : "Servizio non raggiungibile. Verifica la configurazione."}
              </div>
            </div>
          </li>
          <li className="flex items-center gap-3">
            {ricercaOk ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : ricercaEnabled ? (
              <AlertCircle className="h-4 w-4 text-red-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-slate-400" />
            )}
            <div className="flex-1">
              <div className="text-sm text-slate-800">{ricerca.label}</div>
              <div className="text-xs text-slate-500">
                {ricercaEnabled
                  ? ricercaOk
                    ? "Attiva e raggiungibile."
                    : "Attiva ma non raggiungibile."
                  : "Disattivata: il sistema usa il matching deterministico."}
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}
